import { useState, useCallback, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search, Plus, MoreHorizontal, ArrowLeft, Loader2 } from "lucide-react";
import { Table, Badge } from "@radix-ui/themes";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DeviceDetailModal } from "@/components/DeviceDetailModal";
import { PageHeader } from "@/components/PageHeader";
import { DeviceFilters, DeviceFilterOptions } from "@/components/DeviceFilters";
import { useConvexDevices } from "@/hooks/useConvexDevices";
import { getDeviceStatusInfo, getRadixBadgeColor, type DeviceStatus } from "@/utils/deviceStatus";
import { ErrorBoundary } from "./ErrorBoundary";
import { ConvexErrorBoundary } from "./ConvexErrorBoundary";

// Remove duplicate Device interface - use the one from useConvexDevices hook
export const DevicesSection = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [selectedDevice, setSelectedDevice] = useState<any>(null); // Use any to avoid interface conflicts
  const [isFullScreenView, setIsFullScreenView] = useState(false);
  const [filters, setFilters] = useState<DeviceFilterOptions>({
    status: [],
    lastSyncRange: "all",
  });
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const {
    devices: allDevices,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    searchTerm,
    setSearchTerm,
    totalCount
  } = useConvexDevices();

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading && !isLoadingMore) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, isLoading, isLoadingMore, loadMore]);

  // Filter devices based on current filter settings
  const devices = (allDevices || []).filter(device => {
    // Status filter
    if (filters.status.length > 0 && !filters.status.includes(device.status)) {
      return false;
    }

    // Note: Last sync filter is simplified since we don't have actual sync timestamps
    // This can be enhanced when real sync data is available
    return true;
  });

  // Check URL for selected device on mount and when devices change - FIXED: use allDevices instead of devices
  useEffect(() => {
    const deviceId = searchParams.get('deviceId');
    if (deviceId && allDevices?.length > 0) {
      const device = allDevices.find(d => d.id === deviceId);
      if (device) {
        setSelectedDevice(device);
        setIsFullScreenView(true);
      }
    }
  }, [searchParams, allDevices || []]); // Ensure allDevices is always an array to prevent undefined length errors

  const handleDeviceClick = (device: any) => {
    setSelectedDevice(device);
    setIsFullScreenView(true);
    
    // Update URL with device ID
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('deviceId', device.id);
    setSearchParams(newSearchParams);
  };

  const handleBackToTable = () => {
    setIsFullScreenView(false);
    setSelectedDevice(null);
    
    // Check if we should return to a specific location
    const returnTo = searchParams.get('returnTo');
    const userId = searchParams.get('userId');
    
    if (returnTo === 'users' && userId) {
      // Navigate back to users section with the user detail open
      navigate(`/section/${returnTo}?userId=${userId}`);
    } else {
      // Remove device ID from URL and stay on devices page
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('deviceId');
      newSearchParams.delete('tab');
      newSearchParams.delete('returnTo');
      newSearchParams.delete('userId');
      setSearchParams(newSearchParams);
    }
  };

  // Full screen device view
  if (isFullScreenView && selectedDevice) {
    return (
      <>
        <PageHeader
          title=""
          device={selectedDevice}
          showBackButton={true}
          onBack={handleBackToTable}
        />
        <div className="h-full flex flex-col">
          <DeviceDetailModal 
            device={selectedDevice} 
            onClose={handleBackToTable}
          />
        </div>
      </>
    );
  }

  // Table view with updated columns
  return (
    <ConvexErrorBoundary>
      <ErrorBoundary>
      <div className="space-y-6 px-6 pb-6 pt-[1.375rem]">
      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search devices..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-black/20"
          />
        </div>
        <DeviceFilters 
          currentFilters={filters}
          onFiltersChange={setFilters}
        />
      </div>

      {devices.length === 0 && !isLoading ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No devices found</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm || filters.status.length > 0 || filters.lastSyncRange !== "all" 
              ? "No devices match your current search and filter criteria" 
              : "No devices are currently enrolled in Convex."}
          </p>
          <Button className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Device
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <Table.Root variant="surface" size="2">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Device</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="hidden sm:table-cell">User</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="hidden md:table-cell">Serial Number</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell className="hidden lg:table-cell">JSS ID</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell></Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
                {isLoading ? (
                  // Loading skeleton
                  [...Array(5)].map((_, i) => (
                    <Table.Row key={i}>
                      <Table.Cell><div className="h-4 bg-gray-200 rounded animate-pulse"></div></Table.Cell>
                      <Table.Cell className="hidden sm:table-cell"><div className="h-4 bg-gray-200 rounded animate-pulse"></div></Table.Cell>
                      <Table.Cell><div className="h-4 bg-gray-200 rounded animate-pulse"></div></Table.Cell>
                      <Table.Cell className="hidden md:table-cell"><div className="h-4 bg-gray-200 rounded animate-pulse"></div></Table.Cell>
                      <Table.Cell className="hidden lg:table-cell"><div className="h-4 bg-gray-200 rounded animate-pulse"></div></Table.Cell>
                      <Table.Cell></Table.Cell>
                    </Table.Row>
                  ))
                ) : (
                  devices.map((device) => {
                    const statusInfo = getDeviceStatusInfo(device.status);
                    
                    return (
                      <Table.Row 
                        key={device.id}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => handleDeviceClick(device)}
                      >
                        <Table.Cell>
                          <div className="truncate">
                            <div className="font-medium text-gray-900 text-sm">{device.name}</div>
                            <div className="text-xs text-gray-600 sm:hidden truncate">
                              {device.user}
                            </div>
                          </div>
                        </Table.Cell>
                        <Table.Cell className="text-gray-600 hidden sm:table-cell text-sm">
                          {device.user}
                        </Table.Cell>
                        <Table.Cell>
                          <Badge color={getRadixBadgeColor(device.status)} size="1">
                            {statusInfo.label}
                          </Badge>
                        </Table.Cell>
                        <Table.Cell className="text-gray-600 hidden md:table-cell text-sm">
                          {device.serial || "Not available"}
                        </Table.Cell>
                        <Table.Cell className="text-gray-600 hidden lg:table-cell text-sm">
                          {device.jssId || "Not available"}
                        </Table.Cell>
                        <Table.Cell>
                          <button className="text-gray-400 hover:text-gray-600">
                            <MoreHorizontal className="w-4 h-4" />
                          </button>
                        </Table.Cell>
                      </Table.Row>
                    );
                  })
                )}
            </Table.Body>
          </Table.Root>
        </div>
      )}

      {/* Load more trigger */}
      <div ref={loadMoreRef} className="py-4 flex items-center justify-center">
        {isLoadingMore && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Loading more devices...</span>
          </div>
        )}
        {!isLoadingMore && hasMore && (
          <Button variant="outline" onClick={loadMore} size="sm">
            Load More
          </Button>
        )}
      </div>

      {devices.length > 0 && (
        <div className="flex items-center justify-center text-sm text-muted-foreground pb-6">
          <span>
            Showing {devices.length} devices{hasMore ? " (scroll for more)" : ""} (sourced from Convex)
            {(filters.status.length > 0 || filters.lastSyncRange !== "all") && 
              " - filtered results"}
          </span>
        </div>
      )}
      </div>
    </ErrorBoundary>
    </ConvexErrorBoundary>
  );
};
